import { useEffect, useMemo, useState } from "react";
import { FunctionField, ListContextProvider, useList, useListContext, useNotify, useUnselectAll } from "react-admin";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import BalanceOutlined from "@mui/icons-material/BalanceOutlined";
import CalendarMonthOutlined from "@mui/icons-material/CalendarMonthOutlined";
import CloudUploadOutlined from "@mui/icons-material/CloudUploadOutlined";
import EventNoteOutlined from "@mui/icons-material/EventNoteOutlined";
import FileDownloadOutlined from "@mui/icons-material/FileDownloadOutlined";
import SendOutlined from "@mui/icons-material/SendOutlined";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { type Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { format, parseISO } from "date-fns";
import { SectionCard } from "@/components/common/SectionCard";
import { ListEmptyState } from "@/components/common/ListEmptyState";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { PaginatedDatagrid } from "@/components/ui/react-admin/PaginatedDatagrid";
import { ConfirmationDialog } from "@/components/ui/ConfirmationDialog";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { useRichApiErrorNotify } from "@/hooks/useRichApiErrorNotify";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { usePolicyContext, useSelectedPolicyId } from "@/resources/claimants/components/PolicyContext";
import { useEndorsements } from "@/resources/policies/hooks/useEndorsements";
import type { AccommodationRun, AggregateAccommodationConfig } from "@/types";
import { useAccommodationRuns } from "./useAccommodationRuns";
import { useAccommodationEnrollmentGate } from "./useAccommodationEnrollmentGate";
import { AccommodationEnrollmentGate } from "./AccommodationEnrollmentGate";
import { AccommodationRunExpandPanel } from "./AccommodationRunExpandPanel";
import { downloadAccommodationRunsCSV } from "./export/exportAggregateData";

// Story #4629: month picker uses UTC throughout so the user's local timezone can't
// drift the picked month from what Postgres stores in the run_month column.
dayjs.extend(utc);

type DialogState = "calculate" | "submitAura" | "submitCarrier" | "reconcile" | null;

/** Stable resource label for react-admin's selection store. The accommodation
 * table isn't a top-level react-admin resource (it's a nested view inside the
 * client page), so we pick our own key to scope selectedIds and avoid colliding
 * with the parent "policies" resource selection. */
const SELECTION_RESOURCE = "accommodation-runs";

export const AccommodationTable = () => {
  const policyId = useSelectedPolicyId();
  const { selectedPolicy } = usePolicyContext();
  const [dialogOpen, setDialogOpen] = useState<DialogState>(null);
  const [expanded, setExpanded] = useState(true);
  const [showSuperseded, setShowSuperseded] = useState(false);
  // IDs captured at the moment the user clicks "Submit to Aura" — pinned for
  // the dialog so the confirm message stays in sync with the bulk-action button
  // press even if the user toggles selection in the background.
  const [pendingSubmitIds, setPendingSubmitIds] = useState<string[]>([]);
  // Story #4629: picked run month for the manual monthly calc dialog. Stored as a
  // Dayjs since the app's LocalizationProvider uses AdapterDayjs. Reset to maxDate
  // each time the dialog opens (see effect below).
  const [pickedRunMonth, setPickedRunMonth] = useState<Dayjs | null>(null);
  const notify = useNotify();
  const notifyError = useRichApiErrorNotify();
  const unselectAll = useUnselectAll(SELECTION_RESOURCE);
  const { runs, isLoading, isError, triggerCalculation, isTriggering, reconcile, isReconciling } =
    useAccommodationRuns(policyId);
  const sortedRuns = useMemo(
    () => [...runs].sort((a, b) => new Date(b.run_date).getTime() - new Date(a.run_date).getTime()),
    [runs]
  );
  // Story #4504: by default hide superseded rows (re-runs leave them queryable for audit
  // but they shouldn't clutter the day-to-day view). Toggle reveals them.
  const visibleRuns = useMemo(
    () => (showSuperseded ? sortedRuns : sortedRuns.filter((r) => !r.is_superseded)),
    [sortedRuns, showSuperseded]
  );
  const supersededCount = useMemo(() => sortedRuns.filter((r) => r.is_superseded).length, [sortedRuns]);

  // Identify the active reconciliation run (if any). Prefer the typed run_type field;
  // fall back to the legacy clawback/reimbursement heuristic for any rows that predate
  // the run_type column being populated. Used both for the year-end banner and to
  // disable the "Reconcile Year-End" button when an active reconciliation already exists.
  //
  // TODO(4504): once the backend has backfilled run_type on existing rows in all
  // environments, drop the clawback/reimbursement fallback and key only off
  // `r.run_type === "reconciliation"`. The fallback is here for deploy-window rows
  // that landed before migration 000128 (no production data in dev/uat at the time
  // of that migration, so this is mostly defensive — track the cleanup in a
  // follow-up issue once dev/uat are confirmed clean).
  const activeReconciliationRun = useMemo(
    () =>
      sortedRuns.find(
        (r) =>
          !r.is_superseded &&
          (r.run_type === "reconciliation" || r.clawback_amount > 0 || r.additional_reimbursement_amount > 0)
      ),
    [sortedRuns]
  );

  // Story #4635: cumulative YTD funded amount = sum of `funding_amount` across
  // non-superseded *monthly* runs for the policy. Used both in the year-end
  // reconciliation banner (in place of the reconciliation run's own
  // `funding_amount`, which is actually `additional_reimbursement` per the
  // backend handler and was misleading when the year-end netted to a clawback)
  // and as a candidate for the summary tile's headline. Reconciliation runs are
  // excluded — their funding_amount belongs to a different ledger.
  const totalMonthlyFunded = useMemo(
    () =>
      sortedRuns
        .filter((r) => r.run_type === "monthly" && !r.is_superseded)
        .reduce((sum, r) => sum + r.funding_amount, 0),
    [sortedRuns]
  );

  // Story #4629: month picker bounds for the "Run Monthly Calculation" dialog.
  //
  // - maxPickableMonth = min(firstOfMonth(today) − 1 month, firstOfMonth(policy
  //   effective_end_date)). Current month and future are not selectable; for an
  //   expired policy the upper bound clamps further to the policy's last month.
  //   Backend PR #3499 review #16244 enforces the policy-end half of this rule
  //   on the API; without the FE clamp, an expired policy would let the user
  //   pick a month past policy end and the backend would 400.
  // - latestActiveMonthlyRun = most recent non-superseded monthly run for this policy.
  // - minPickableMonth = max(firstOfMonth(policy.effective_start_date),
  //   latestActiveMonthlyRun.run_month). The sequence rule prevents running a month
  //   earlier than the latest existing run; re-running latestActiveMonthlyRun itself
  //   is allowed and flows through the supersede path.
  const latestActiveMonthlyRun = useMemo(
    () => sortedRuns.find((r) => r.run_type === "monthly" && !r.is_superseded),
    [sortedRuns]
  );
  const maxPickableMonth = useMemo(() => {
    const lastCompletedMonth = dayjs.utc().startOf("month").subtract(1, "month");
    const policyEnd = selectedPolicy?.effective_end_date
      ? dayjs.utc(selectedPolicy.effective_end_date).startOf("month")
      : undefined;
    if (policyEnd && policyEnd.isBefore(lastCompletedMonth)) {
      return policyEnd;
    }
    return lastCompletedMonth;
  }, [selectedPolicy]);
  const minPickableMonth = useMemo(() => {
    const policyStart = selectedPolicy?.effective_start_date
      ? dayjs.utc(selectedPolicy.effective_start_date).startOf("month")
      : undefined;
    const latestRunMonth = latestActiveMonthlyRun
      ? dayjs.utc(latestActiveMonthlyRun.run_month).startOf("month")
      : undefined;
    if (policyStart && latestRunMonth) {
      return policyStart.isAfter(latestRunMonth) ? policyStart : latestRunMonth;
    }
    return policyStart ?? latestRunMonth;
  }, [selectedPolicy, latestActiveMonthlyRun]);

  // Re-run warning: shown when the picked month equals an existing non-superseded
  // monthly run (in practice, only possible when pickedRunMonth == latestActiveMonthlyRun
  // because the sequence rule blocks earlier months at the picker level).
  const pickedMonthExistingRun = useMemo(() => {
    if (!pickedRunMonth) return undefined;
    const pickedISO = pickedRunMonth.startOf("month").format("YYYY-MM-DD");
    return sortedRuns.find((r) => r.run_month === pickedISO && r.run_type === "monthly" && !r.is_superseded);
  }, [pickedRunMonth, sortedRuns]);

  // TODO(perm): replace with a dedicated 'calculate' permission once role gating is defined
  const { canEdit } = useResourcePermissions("policies");
  const { getByCategory } = useEndorsements(policyId ?? "");
  const accommodationEndorsements = getByCategory("aggregate_accommodation");
  const hasAggregateAccommodation = accommodationEndorsements.some((e) => e.is_currently_active);
  // A flat annual attachment doesn't read enrollment, so the run dialogs
  // show it read-only and don't require it (the backend skips its gate too).
  const accommodationConfig = (
    accommodationEndorsements.find((e) => e.is_currently_active) ?? accommodationEndorsements[0]
  )?.config as AggregateAccommodationConfig | null | undefined;
  const isFlatAttachment = typeof accommodationConfig?.annual_attachment_override === "number";

  // Enrollment grid for the run dialogs. The monthly run reads policy start
  // through the picked month; year-end reads the whole term.
  const enrollmentGate = useAccommodationEnrollmentGate({
    policyId,
    effectiveStart: selectedPolicy?.effective_start_date,
    effectiveEnd: selectedPolicy?.effective_end_date,
    throughMonth:
      dialogOpen === "calculate" && pickedRunMonth ? pickedRunMonth.startOf("month").format("YYYY-MM-DD") : undefined,
    isFlatAttachment,
  });
  const resetEnrollmentGate = enrollmentGate.reset;

  // Reset the picker to its default (maxPickableMonth = last completed month) each
  // time the dialog opens. The clamp depends on the latest run, which can change
  // between dialog opens (e.g., a successful run advances minPickableMonth forward).
  useEffect(() => {
    if (dialogOpen === "calculate") {
      setPickedRunMonth(maxPickableMonth);
    }
  }, [dialogOpen, maxPickableMonth]);

  // Opening either run dialog discards unsaved enrollment entries. Keyed on
  // the dialog only, so a policy refetch while it's open doesn't wipe them.
  useEffect(() => {
    if (dialogOpen === "calculate" || dialogOpen === "reconcile") {
      resetEnrollmentGate();
    }
  }, [dialogOpen, resetEnrollmentGate]);

  // Selected-run details for the Submit to Aura confirm dialog. Pulled from
  // sortedRuns by ID so superseded/recon rows that somehow slipped through
  // isRowSelectable still resolve to a sensible message instead of undefined.
  const submitSelectedRuns = useMemo(
    () => pendingSubmitIds.map((id) => sortedRuns.find((r) => r.id === id)).filter((r): r is AccommodationRun => !!r),
    [pendingSubmitIds, sortedRuns]
  );
  const submitTotalFunding = useMemo(
    () => submitSelectedRuns.reduce((sum, r) => sum + r.funding_amount, 0),
    [submitSelectedRuns]
  );
  const submitMonthList = useMemo(
    () =>
      submitSelectedRuns
        .map((r) => ({ run_month: r.run_month, label: format(parseISO(r.run_month), "MMMM yyyy") }))
        .sort((a, b) => a.run_month.localeCompare(b.run_month))
        .map((r) => r.label),
    [submitSelectedRuns]
  );

  // AC6: when the user switches policies the parent supplies a new `policyId`.
  // Drop any stale selectedIds in the react-admin store so the floating bulk
  // toolbar doesn't carry over from one policy's runs to the next.
  useEffect(() => {
    unselectAll();
    setPendingSubmitIds([]);
  }, [policyId, unselectAll]);

  // Build a synthetic ListContext so `<Datagrid>` (rendered by
  // `<PaginatedDatagrid>`) wires up the floating bulk-action toolbar and
  // per-row checkboxes. `useList` itself paginates locally, so we set
  // `perPage` high enough to keep all `visibleRuns` on one logical page —
  // PaginatedDatagrid still handles the displayed pagination via its own
  // slicing. Selection state is scoped by `resource` to SELECTION_RESOURCE so
  // it doesn't collide with the parent "policies" view.
  const listContext = useList({
    data: visibleRuns,
    isLoading,
    perPage: Math.max(visibleRuns.length, 1),
    sort: { field: "run_date", order: "DESC" },
    resource: SELECTION_RESOURCE,
  });

  if (!policyId) return null;

  const handleOpenSubmitAura = (ids: string[]) => {
    setPendingSubmitIds(ids);
    setDialogOpen("submitAura");
  };

  // The dialogs stay open until the run returns. Enrollment entered in the
  // dialog is saved with the run, so on failure the user keeps their entries.
  const handleCalculateConfirm = () => {
    if (!pickedRunMonth) return;
    const runMonthISO = pickedRunMonth.startOf("month").format("YYYY-MM-DD");
    triggerCalculation(
      { runMonth: runMonthISO, enrollments: enrollmentGate.enrollmentsToSave },
      {
        onSuccess: () => {
          setDialogOpen(null);
          notify("Monthly calculation completed successfully", { type: "success" });
        },
        onError: (error) => notifyError(error),
      }
    );
  };

  const handleReconcileConfirm = () => {
    reconcile(
      { enrollments: enrollmentGate.enrollmentsToSave },
      {
        onSuccess: () => {
          setDialogOpen(null);
          notify("Year-end reconciliation completed successfully", { type: "success" });
        },
        onError: (error) => notifyError(error),
      }
    );
  };

  const handleSubmitAuraConfirm = () => {
    // TODO(4636-followup): add isSubmitting guard before wiring the real POST.
    // The stub is idempotent (notify + clear), but a rapid double-click on the
    // ConfirmationDialog's confirm button will fire this handler twice before
    // React re-renders. Harmless today; becomes a double-POST once Aura wiring
    // lands. Either add a local `isSubmitting` state here, or move the disabled
    // logic onto the dialog's confirm button itself.
    setDialogOpen(null);
    const count = pendingSubmitIds.length;
    notify(
      count > 0
        ? `Submission queued — Aura integration coming soon (${count} ${count === 1 ? "run" : "runs"})`
        : "Submission feature coming soon",
      { type: "info" }
    );
    unselectAll();
    setPendingSubmitIds([]);
  };

  const handleSubmitCarrierConfirm = () => {
    setDialogOpen(null);
    notify("Submission feature coming soon", { type: "info" });
  };

  // Export uses visibleRuns (matches what the user sees in the table). If the user
  // wants superseded rows in the CSV they toggle "Show superseded runs" first, then
  // export — same mental model as filtering on a datagrid. The supersede audit trail
  // remains available via the API for consumers that need full history.
  const handleExportCSV = () => downloadAccommodationRunsCSV(visibleRuns, policyId);

  return (
    <>
      <SectionCard
        title="Month-over-Month Accommodation"
        subtitle={!hasAggregateAccommodation ? "Aggregate accommodation is not enabled for this policy" : undefined}
        collapsible
        isExpanded={expanded}
        onToggle={() => setExpanded((e) => !e)}
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            {canEdit && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CalendarMonthOutlined />}
                  onClick={() => setDialogOpen("calculate")}
                  disabled={!hasAggregateAccommodation || isTriggering}
                >
                  Run Monthly Calculation
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="warning"
                  startIcon={<BalanceOutlined />}
                  onClick={() => setDialogOpen("reconcile")}
                  disabled={!hasAggregateAccommodation || isReconciling}
                >
                  Reconcile Year-End
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<FileDownloadOutlined />}
              onClick={handleExportCSV}
              disabled={sortedRuns.length === 0}
            >
              Export CSV
            </Button>
          </Box>
        }
      >
        <Box sx={!hasAggregateAccommodation ? { opacity: 0.4, pointerEvents: "none" } : undefined}>
          {isError ? (
            <Alert severity="error">Failed to load accommodation runs. Please refresh and try again.</Alert>
          ) : (
            <>
              {activeReconciliationRun && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <strong>Year-End Reconciliation</strong> — Total Funded: {formatUSD(totalMonthlyFunded)} · Clawback:{" "}
                  {formatUSD(activeReconciliationRun.clawback_amount)} · Additional Reimbursement:{" "}
                  {formatUSD(activeReconciliationRun.additional_reimbursement_amount)}
                </Alert>
              )}
              {supersededCount > 0 && (
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        size="small"
                        checked={showSuperseded}
                        onChange={(e) => setShowSuperseded(e.target.checked)}
                      />
                    }
                    label={
                      <Typography variant="body2" color="text.secondary">
                        Show superseded runs ({supersededCount})
                      </Typography>
                    }
                  />
                </Box>
              )}
              <ListContextProvider value={listContext}>
                <PaginatedDatagrid
                  data={visibleRuns}
                  isLoading={isLoading}
                  sort={{ field: "run_date", order: "DESC" }}
                  resource={SELECTION_RESOURCE}
                  expand={<AccommodationRunExpandPanel />}
                  rowClick="expand"
                  bulkActionButtons={<SubmitToAuraBulkAction onSubmit={handleOpenSubmitAura} />}
                  isRowSelectable={(record: AccommodationRun) => record.run_type === "monthly" && !record.is_superseded}
                  empty={
                    <ListEmptyState
                      icon={EventNoteOutlined}
                      message="No accommodation runs yet"
                      subtitle="Runs appear here after the first monthly calculation."
                    />
                  }
                >
                  <FunctionField
                    source="run_date"
                    label="Month"
                    render={(record: AccommodationRun) => format(parseISO(record.run_date), "MMMM yyyy")}
                  />
                  <FunctionField
                    source="funding_amount"
                    label="Eligible Amount"
                    render={(record: AccommodationRun) => formatUSD(record.funding_amount)}
                  />
                  <FunctionField
                    source="prorated_attachment"
                    label="Submission Threshold"
                    render={(record: AccommodationRun) => formatUSD(record.prorated_attachment)}
                  />
                  <FunctionField
                    source="is_superseded"
                    label="Status"
                    render={(record: AccommodationRun) =>
                      record.is_superseded ? (
                        <StatusChip label="Superseded" size="small" color="muted" />
                      ) : (
                        <StatusChip label="Active" size="small" color="success" />
                      )
                    }
                  />
                </PaginatedDatagrid>
              </ListContextProvider>
            </>
          )}
          <Box sx={{ display: "flex", gap: 1, mt: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SendOutlined />}
              onClick={() => setDialogOpen("submitCarrier")}
              disabled={!hasAggregateAccommodation}
            >
              Submit to Carrier
            </Button>
          </Box>
        </Box>
      </SectionCard>

      <ConfirmationDialog
        open={dialogOpen === "reconcile"}
        title={activeReconciliationRun ? "Re-run Year-End Reconciliation?" : "Reconcile Year-End"}
        message={
          activeReconciliationRun
            ? `An active year-end reconciliation already exists for this policy (run on ${format(parseISO(activeReconciliationRun.run_date), "MMM d, yyyy")}, clawback ${formatUSD(activeReconciliationRun.clawback_amount)}, additional reimbursement ${formatUSD(activeReconciliationRun.additional_reimbursement_amount)}). Re-running will archive the prior reconciliation as superseded — its numbers stay queryable for audit but no longer count toward funding totals. Proceed?`
            : "This will run year-end reconciliation for this policy. Clawback and additional reimbursement amounts will be calculated. Continue?"
        }
        confirmColor="warning"
        onConfirm={handleReconcileConfirm}
        onCancel={() => setDialogOpen(null)}
        isLoading={isReconciling}
        confirmDisabled={enrollmentGate.isBlocked}
      >
        <Box sx={{ mt: 2 }}>
          <AccommodationEnrollmentGate gate={enrollmentGate} />
        </Box>
      </ConfirmationDialog>
      <Dialog
        open={dialogOpen === "calculate"}
        onClose={() => {
          if (!isTriggering) setDialogOpen(null);
        }}
        maxWidth="xs"
        fullWidth
        aria-label="Run Monthly Calculation dialog"
      >
        <DialogTitle>{pickedMonthExistingRun ? "Re-run Monthly Calculation?" : "Run Monthly Calculation"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Pick the month to run. Only completed months are selectable; the current month and future months are not
              allowed.
            </Typography>
            <DatePicker
              label="Run month"
              views={["year", "month"]}
              openTo="month"
              value={pickedRunMonth}
              onChange={(value) => setPickedRunMonth(value)}
              minDate={minPickableMonth}
              maxDate={maxPickableMonth}
              format="MMMM YYYY"
              slotProps={{
                textField: { fullWidth: true, size: "small" },
              }}
            />
            {pickedMonthExistingRun ? (
              <Alert severity="warning">
                An active monthly run already exists for{" "}
                {format(parseISO(pickedMonthExistingRun.run_month), "MMMM yyyy")} (run on{" "}
                {format(parseISO(pickedMonthExistingRun.run_date), "MMM d, yyyy")}, funded{" "}
                {formatUSD(pickedMonthExistingRun.funding_amount)}). Re-running will archive the prior run as superseded
                — its numbers stay queryable for audit but no longer count toward funding totals.
              </Alert>
            ) : (
              <Typography variant="body2" color="text.secondary">
                YTD spend will be aggregated through the end of the selected month.
              </Typography>
            )}
            <AccommodationEnrollmentGate gate={enrollmentGate} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(null)} disabled={isTriggering}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pickedMonthExistingRun ? "warning" : "primary"}
            onClick={handleCalculateConfirm}
            disabled={!pickedRunMonth || isTriggering || enrollmentGate.isBlocked}
          >
            {pickedMonthExistingRun ? "Re-run" : "Run"}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={dialogOpen === "submitAura"}
        title="Submit to Aura"
        message={
          submitSelectedRuns.length > 0
            ? `Submit ${submitSelectedRuns.length} ${submitSelectedRuns.length === 1 ? "run" : "runs"} (${submitMonthList.join(", ")}) to Aura for reimbursement? Total funding: ${formatUSD(submitTotalFunding)}.`
            : "Submit selected accommodation runs to Aura for reimbursement?"
        }
        onConfirm={handleSubmitAuraConfirm}
        onCancel={() => {
          setDialogOpen(null);
          setPendingSubmitIds([]);
        }}
      />
      <ConfirmationDialog
        open={dialogOpen === "submitCarrier"}
        title="Submit to Carrier"
        message="Submit selected accommodation runs to the carrier?"
        onConfirm={handleSubmitCarrierConfirm}
        onCancel={() => setDialogOpen(null)}
      />
    </>
  );
};

/**
 * Floating bulk-action button rendered by `<Datagrid>` when the user selects
 * one or more rows. Reads `selectedIds` from the surrounding `<ListContext>`
 * (provided in `AccommodationTable`) and hands them to the parent so the
 * confirm dialog can be built with the captured set.
 *
 * Kept file-local — promote only if a second consumer emerges.
 */
const SubmitToAuraBulkAction = ({ onSubmit }: { onSubmit: (ids: string[]) => void }) => {
  const { selectedIds } = useListContext();
  const ids = (selectedIds as string[]) ?? [];
  return (
    <Button size="small" startIcon={<CloudUploadOutlined />} onClick={() => onSubmit(ids)}>
      Submit to Aura{ids.length > 0 ? ` (${ids.length})` : ""}
    </Button>
  );
};
